import { useState } from 'react';
import { LODGING_TYPES, LODGING_TYPE_LABELS } from '@camper-diary/shared';
import { AddressSearch } from './AddressSearch';
import type { Geo } from '../api/geocoding';
import { parseCoordinate } from '../utils/coordinates';

/**
 * Stato del form Alloggio: stringhe pure (lat/lon arrivano come testo per
 * supportare input parziali e formati DMS). Il caller li converte in numero
 * solo al momento del submit.
 */
export type LodgingFormState = {
  name: string;
  type: string; // '' | 'CAMPEGGIO' | 'AREA_CAMPER' | 'LIBERA'
  phone: string;
  url: string;
  lat: string;
  lon: string;
  notes: string;
  // Se true (e con coordinate alloggio valide), i km fra le tappe vengono
  // calcolati arrivando/partendo dall'alloggio invece che dal luogo da visitare.
  useForRouting: boolean;
};

type Props = {
  value: LodgingFormState;
  onChange: (next: LodgingFormState) => void;
  /** Coordinate della tappa "principale" — se valorizzate, si abilita il
   *  bottone "Usa coordinate tappa" per copiarle nei campi alloggio. */
  stageLat?: string;
  stageLon?: string;
};

/**
 * Pannello collassabile "Alloggio" da incorporare in qualsiasi form tappa.
 * Si apre automaticamente se almeno un campo è già valorizzato (caso edit di
 * una tappa che ha già un alloggio).
 */
export function LodgingFields({ value, onChange, stageLat, stageLon }: Props) {
  const hasAnyValue =
    Boolean(value.name || value.type || value.phone || value.url || value.lat || value.lon || value.notes);
  const [open, setOpen] = useState<boolean>(hasAnyValue);

  // Query corrente nel campo di ricerca alloggio. Stato LOCALE perché il
  // testo digitato durante la ricerca non va persistito: ci interessano solo
  // lat/lon e nome che vengono iniettati nel form alla selezione del
  // risultato. AddressSearch è un input controllato, quindi senza questo
  // stato il campo resterebbe vuoto a ogni keystroke (bug precedente).
  const [searchQuery, setSearchQuery] = useState('');

  function set<K extends keyof LodgingFormState>(k: K, v: LodgingFormState[K]) {
    onChange({ ...value, [k]: v });
  }

  function onAddressPick(r: Geo) {
    onChange({
      ...value,
      lat: r.lat.toFixed(6),
      lon: r.lon.toFixed(6),
      // riempie il "nome" solo se l'utente non l'ha già scritto: il primo
      // segmento del display name è in genere il nome del posto
      ...(value.name.trim() === '' && { name: r.displayName.split(',')[0]?.trim() ?? '' }),
    });
    // resetta la query: la search bar torna pronta per un'altra ricerca
    setSearchQuery('');
  }

  function copyFromStage() {
    if (!stageLat || !stageLon) return;
    onChange({ ...value, lat: stageLat, lon: stageLon });
  }

  function clearCoords() {
    // Senza coordinate il flag "usa per i km" non ha senso: azzeralo.
    onChange({ ...value, lat: '', lon: '', useForRouting: false });
  }

  function clearAll() {
    onChange({
      name: '', type: '', phone: '', url: '', lat: '', lon: '', notes: '',
      useForRouting: false,
    });
    setSearchQuery('');
  }

  // Parse coordinate per il badge: mostra solo se entrambe valide.
  const latNum = value.lat.trim() ? parseCoordinate(value.lat) : null;
  const lonNum = value.lon.trim() ? parseCoordinate(value.lon) : null;
  const hasCoords =
    latNum !== null && lonNum !== null &&
    latNum >= -90 && latNum <= 90 && lonNum >= -180 && lonNum <= 180;

  return (
    <div className={`lodging-fields${open ? ' open' : ''}`}>
      <button
        type="button"
        className="lodging-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="lodging-toggle-arrow">{open ? '▾' : '▸'}</span>
        🛏️ Alloggio {hasAnyValue && !open && <span className="muted">(compilato)</span>}
      </button>

      {open && (
        <div className="lodging-body">
          <p className="muted form-hint">
            Dove dormi durante questa tappa. Le coordinate alimentano solo il
            pulsante <strong>Naviga all'alloggio</strong>: non viene aggiunto
            alcun marker sulla mappa.
          </p>

          <div className="row">
            <label>
              <span>Nome (es. Camping Sokol Praha)</span>
              <input
                type="text"
                value={value.name}
                onChange={(e) => set('name', e.target.value)}
                maxLength={200}
              />
            </label>
            <label>
              <span>Tipo</span>
              <select value={value.type} onChange={(e) => set('type', e.target.value)}>
                <option value="">—</option>
                {LODGING_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {LODGING_TYPE_LABELS[t].icon} {LODGING_TYPE_LABELS[t].label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="row">
            <label>
              <span>Telefono</span>
              <input
                type="tel"
                value={value.phone}
                onChange={(e) => set('phone', e.target.value)}
                maxLength={30}
                placeholder="+39 333 1234567"
                inputMode="tel"
              />
            </label>
            <label>
              <span>Sito / link prenotazione</span>
              <input
                type="url"
                value={value.url}
                onChange={(e) => set('url', e.target.value)}
                maxLength={500}
                placeholder="https://…"
              />
            </label>
          </div>

          <label>
            <span>Posizione alloggio (cerca o seleziona)</span>
            <AddressSearch
              value={searchQuery}
              onChange={setSearchQuery}
              onPick={onAddressPick}
              placeholder="Cerca il campeggio / area sosta…"
            />
          </label>

          <div className="lodging-coord-row">
            {hasCoords ? (
              <span className="lodging-coord-badge">
                📍 <strong>{latNum!.toFixed(5)}, {lonNum!.toFixed(5)}</strong>
                <button
                  type="button"
                  className="lodging-coord-clear"
                  onClick={clearCoords}
                  title="Rimuovi posizione alloggio"
                  aria-label="Rimuovi posizione alloggio"
                >
                  ✕
                </button>
              </span>
            ) : (
              <span className="muted small">
                Nessuna posizione — il pulsante "Naviga all'alloggio" comparirà solo
                quando avrai selezionato un risultato.
              </span>
            )}
            {stageLat && stageLon && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={copyFromStage}>
                📍 Usa coordinate tappa
              </button>
            )}
          </div>

          {hasCoords && (
            <label className={`lodging-routing-toggle${value.useForRouting ? ' on' : ''}`}>
              <input
                type="checkbox"
                checked={value.useForRouting}
                onChange={(e) => set('useForRouting', e.target.checked)}
              />
              <span className="lrt-text">
                <span className="lrt-title">📏 Calcola i km fino a questo alloggio</span>
                <span className="lrt-sub">
                  Il percorso sulla mappa e le distanze fra le tappe arrivano e
                  ripartono da qui, invece che dal luogo da visitare.
                </span>
              </span>
            </label>
          )}

          <label>
            <span>Note (numero prenotazione, costo a notte, accessibilità camper…)</span>
            <textarea
              rows={3}
              value={value.notes}
              onChange={(e) => set('notes', e.target.value)}
              maxLength={5000}
            />
          </label>

          {hasAnyValue && (
            <div className="lodging-coord-tools">
              <button type="button" className="btn btn-ghost btn-sm" onClick={clearAll}>
                Svuota alloggio
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
